import { CSSObject, Global } from '@emotion/react';
import {
  FloatingPortal,
  autoUpdate,
  offset as floatingOffset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import { useFlagMe424661EnableV2TabsWithAdobeSpectrumTooltip } from '@generated/flags/ME-424661-enable-v-2-tabs-with-adobe-spectrum-tooltip';
import { useTheme } from '@hooks/useTheme';
// eslint-disable-next-line no-restricted-imports
import { Position, TooltipPopup, useTooltip } from '@reach/tooltip';
import '@reach/tooltip/styles.css';
import { win } from '@utils/win';
import { TOOLTIP } from '@utils/zIndex';
import { isString } from 'lodash-es';
import {
  FC,
  HTMLAttributes,
  ReactElement,
  ReactNode,
  cloneElement,
  useState,
} from 'react';

const VISUAL_MARGIN = 8;

const centered: Position = (triggerRect, tooltipRect) => {
  const triggerCenter =
    (triggerRect?.left ?? 0) + (triggerRect?.width ?? 0) / 2;
  const left = triggerCenter - (tooltipRect?.width ?? 0) / 2;
  const maxLeft = win.innerWidth - (tooltipRect?.width ?? 0) - 2;
  return {
    left: Math.min(Math.max(2, left), maxLeft) + win.scrollX,
    top: (triggerRect?.bottom ?? 0) + VISUAL_MARGIN + win.scrollY,
  };
};

const top: Position = (triggerRect, tooltipRect) => {
  const triggerCenter =
    (triggerRect?.left ?? 0) + (triggerRect?.width ?? 0) / 2;
  const left = triggerCenter - (tooltipRect?.width ?? 0) / 2;
  const maxLeft = win.innerWidth - (tooltipRect?.width ?? 0) - 2;
  return {
    left: Math.min(Math.max(2, left), maxLeft) + win.scrollX,
    top: (triggerRect?.top ?? 0) - (tooltipRect?.height ?? 0) - 5 + win.scrollY,
  };
};

const topLeft: Position = (triggerRect, tooltipRect) => {
  const triggerRight = (triggerRect?.left ?? 0) + (triggerRect?.width ?? 0);
  const left = triggerRight - (tooltipRect?.width ?? 0);
  return {
    left: Math.max(2, left) + win.scrollX,
    top: (triggerRect?.top ?? 0) - (tooltipRect?.height ?? 0) - 5 + win.scrollY,
  };
};

const bottom: Position = (triggerRect, tooltipRect) => {
  const triggerCenter = (triggerRect?.left ?? 0) + (triggerRect?.width ?? 0);
  const left = triggerCenter - (tooltipRect?.width ?? 0);
  const maxLeft = win.innerWidth - (tooltipRect?.width ?? 0) - 2;
  return {
    left: Math.min(Math.max(2, left), maxLeft) + win.scrollX + 5,
    top: (triggerRect?.bottom ?? 0) + 5 + win.scrollY,
  };
};

const left: Position = (triggerRect) => {
  return {
    left: triggerRect?.left ?? 0,
    top: (triggerRect?.bottom ?? 0) + 5 + win.scrollY,
  };
};

export type PositionType = 'centered' | 'bottom' | 'top' | 'topLeft' | 'left';

const positions = {
  centered,
  bottom,
  top,
  topLeft,
  left,
};

export interface TooltipProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  children: ReactElement;
  label?: ReactNode;
  ariaLabel?: string;
  hidden?: boolean;
  positionType?: PositionType;
  /** @deprecated Reach-era prop kept for backward compatibility. Use positionType instead. */
  position?: Position;
  /** @deprecated Reach-era prop kept for backward compatibility. */
  isVisible?: boolean;
}

// ─── Shared Styles ────────────────────────────────────────────────

const useTooltipStyles = (hidden?: boolean): CSSObject => {
  const { tooltip: tooltipTheme } = useTheme();
  return {
    ...tooltipTheme,
    border: 'none',
    borderRadius: 2,
    padding: '0.5em 1em',
    display: hidden ? 'none' : 'block',
    justifyContent: 'center',
    boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.2)',
    whiteSpace: 'pre-line',
  };
};

// ─── V1: Reach Tooltip ────────────────────────────────────────────

const TooltipV1: FC<TooltipProps> = ({
  children,
  label,
  ariaLabel,
  hidden,
  positionType,
  ...rest
}) => {
  const [trigger, tooltip] = useTooltip();
  const tooltipStyles = useTooltipStyles(hidden);

  return (
    <>
      <Global
        styles={{
          'html [data-reach-tooltip]': {
            zIndex: TOOLTIP,
          },
        }}
      />
      {cloneElement(children, trigger)}
      {label && (
        <TooltipPopup
          {...tooltip}
          css={tooltipStyles}
          position={positions[positionType || 'centered']}
          label={label}
          ariaLabel={ariaLabel || isString(label) ? (label as string) : ''}
          {...rest}
        />
      )}
    </>
  );
};

// ─── V2: Floating UI Tooltip ──────────────────────────────────────

const positionToPlacement = {
  centered: {
    placement: 'bottom' as const,
    mainAxis: VISUAL_MARGIN,
    crossAxis: 0,
  },
  bottom: { placement: 'bottom-end' as const, mainAxis: 5, crossAxis: 5 },
  top: { placement: 'top' as const, mainAxis: 5, crossAxis: 0 },
  topLeft: { placement: 'top-end' as const, mainAxis: 5, crossAxis: 0 },
  left: { placement: 'bottom-start' as const, mainAxis: 5, crossAxis: 0 },
};

const TooltipV2: FC<TooltipProps> = ({
  children,
  label,
  ariaLabel,
  hidden,
  positionType,
  isVisible,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  position,
  style: styleProp,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const tooltipStyles = useTooltipStyles(hidden);

  const { placement, mainAxis, crossAxis } =
    positionToPlacement[positionType || 'centered'];

  const { refs, floatingStyles, context } = useFloating({
    open: (isOpen || isVisible) && !!label && !hidden,
    onOpenChange: setIsOpen,
    placement,
    strategy: 'absolute',
    middleware: [
      floatingOffset({ mainAxis, crossAxis }),
      shift({ padding: 2 }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const hover = useHover(context, { move: false });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ]);

  const childRef = (children as fixMe).ref;
  const mergedRef = useMergeRefs([refs.setReference, childRef]);

  const showTooltip = (isOpen || isVisible) && !!label && !hidden;

  return (
    <>
      {cloneElement(children, {
        ...getReferenceProps({
          ref: mergedRef,
          // Bridge handlers: floating-ui's useHover listens for pointerenter,
          // but Cypress tests fire trigger('mouseover') which only produces
          // mouseenter via React event delegation. These bridge handlers
          // ensure the tooltip opens/closes from mouse events as well.
          onMouseEnter: () => setIsOpen(true),
          onMouseLeave: () => setIsOpen(false),
        }),
        'data-reach-tooltip-trigger': '',
      })}
      {showTooltip && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            role="tooltip"
            data-reach-tooltip=""
            css={{ ...tooltipStyles, zIndex: TOOLTIP }}
            aria-label={ariaLabel ?? (isString(label) ? (label as string) : '')}
            {...rest}
            {...getFloatingProps()}
            style={{ ...floatingStyles, ...styleProp }}
          >
            {label}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

// ─── Exported Tooltip (FF-gated) ──────────────────────────────────

export const Tooltip: FC<TooltipProps> = (props) => {
  const useV2 = useFlagMe424661EnableV2TabsWithAdobeSpectrumTooltip();
  return useV2 ? <TooltipV2 {...props} /> : <TooltipV1 {...props} />;
};

interface ButtonTooltipProps extends TooltipProps {
  hidden?: boolean;
  css?: CSSObject;
  isButtonEnabled?: boolean;
}

/** Helper component when you need to render a tooltip with a button as trigger. Without this component it can be difficult to properly trigger the tooltip display if the button is disabled (due to browser default pointer-events behavior for disabled elements) */
export const ButtonTooltip: FC<ButtonTooltipProps> = ({
  hidden,
  isButtonEnabled = false,
  children,
  css,
  ...rest
}) => (
  <div
    css={{
      display: 'inline-block',
      position: 'relative',
      cursor: hidden || isButtonEnabled ? 'pointer' : 'not-allowed !important',
      ...css,
    }}
  >
    {!isButtonEnabled && children}
    <Tooltip
      {...rest}
      hidden={hidden}
      label={
        isString(rest.label) ? (
          <div dangerouslySetInnerHTML={{ __html: rest.label }} />
        ) : (
          rest.label
        )
      }
    >
      {((): ReactElement => {
        if (isButtonEnabled) {
          return children;
        }
        if (hidden) {
          return <></>;
        }
        return (
          <div
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
            }}
          />
        );
      })()}
    </Tooltip>
  </div>
);
